import React, { useMemo } from "react";
import {
  flexRender,
  getCoreRowModel,
  type ColumnDef,
  useReactTable,
} from "@tanstack/react-table";

import type { BaseRowItem } from "../../components/data-table";
import { formatOrdinalActivityDate } from "./userActivityDateFormat";

export type UserReferralItem = {
  createdAt?: string;
  email?: { address?: string };
};

export type UserActivityReferralRow = BaseRowItem & {
  displayDate: string;
  email: string;
};

function buildRows(referrals: UserReferralItem[]): UserActivityReferralRow[] {
  return referrals.map((ref, index) => ({
    id: ref.email?.address ?? `referral-${index}`,
    displayDate: formatOrdinalActivityDate(ref.createdAt),
    email: ref.email?.address ?? "—",
  }));
}

function referralColClass(id: string): string {
  switch (id) {
    case "displayDate":
      return "w-1/5 min-w-0";
    case "email":
      return "w-3/5 min-w-0";
    default:
      return "min-w-0";
  }
}

const columns: ColumnDef<UserActivityReferralRow, unknown>[] = [
  {
    accessorKey: "displayDate",
    header: () => (
      <span className="text-[#617889] text-[12px]">Date</span>
    ),
    cell: ({ getValue }) => (
      <span className="text-white text-[13px]">{String(getValue())}</span>
    ),
  },
  {
    accessorKey: "email",
    header: () => (
      <span className="text-[#617889] text-[12px]">Email</span>
    ),
    cell: ({ getValue }) => (
      <span className="text-white text-[13px]">{String(getValue())}</span>
    ),
  },
];

export type UserActivityReferralsTableProps = {
  referrals: UserReferralItem[];
  emptyImage: string;
};

export function UserActivityReferralsTable({
  referrals,
  emptyImage,
}: UserActivityReferralsTableProps) {
  const data = useMemo(() => buildRows(referrals), [referrals]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => String(row.id),
  });

  if (referrals.length === 0) {
    return (
      <div className="flex flex-col justify-center h-[550px] items-center">
        <img src={emptyImage} alt="" />
        <h4 className="font-bold text-[#869AA9] pt-2">No recent referrals</h4>
        <p className="text-[#869AA9] pt-2 text-[12px]">
          All user referrals will show up here
        </p>
      </div>
    );
  }

  return (
    <div className="bg-[#192025] overflow-auto scrollbar-thumb-[#60B7F6] scrollbar-track-white scrollbar-thin cursor-pointer lgss:flex lgss:flex-col w-full h-[612px]">
      <table className="w-full">
        <thead>
          {table.getHeaderGroups().map((hg) => (
            <tr
              key={hg.id}
              className="border-b border-[#3A4852]/20 mt-8 pb-4 flex justify-start px-10 items-center"
            >
              {hg.headers.map((header) => (
                <th
                  key={header.id}
                  className={`${referralColClass(header.column.id)} text-left whitespace-nowrap overflow-hidden`}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <React.Fragment key={row.id}>
              <tr className="py-6 w-full flex justify-start items-center px-10">
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className={`${referralColClass(cell.column.id)} text-left whitespace-nowrap overflow-hidden`}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
              <tr className="w-full">
                <td className="mt-1 w-full h-[1px] bg-[#3A4852]/20" colSpan={2} />
              </tr>
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}
